/**
 * FAQ Accordion Component
 *
 * Displays frequently asked questions in an expandable/collapsible accordion.
 * Only one FAQ can be open at a time for better UX.
 */

import { useState } from 'react';
import type { FAQItem } from '../../types/faq';

interface FAQAccordionProps {
  faqs: FAQItem[];
  categoryFilter?: string;
}

export const FAQAccordion: React.FC<FAQAccordionProps> = ({
  faqs,
  categoryFilter
}) => {
  // STATE: Track which FAQ is open (only one at a time)
  const [openId, setOpenId] = useState<string | null>(null);

  // FILTER: By category if provided
  const filteredFAQs = categoryFilter
    ? faqs.filter(faq => faq.category === categoryFilter)
    : faqs;

  // SORT: By order
  const sortedFAQs = [...filteredFAQs].sort((a, b) => a.order - b.order);

  // TOGGLE: Open/close FAQ item
  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="space-y-4">
      {sortedFAQs.map((faq, index) => (
        <div
          key={faq.id}
          className="border-4 border-gray-400 overflow-hidden transition-all duration-200 hover:border-indigo_dye-500 hover:shadow-[4px_4px_0px_0px_rgba(0,60,102,0.3)]"
          data-aos="fade-up"
          data-aos-delay={index * 50}
        >
          {/* QUESTION: Button for accessibility and keyboard navigation */}
          <button
            onClick={() => toggleFAQ(faq.id)}
            className="w-full text-left p-4 font-mono font-bold uppercase tracking-wide flex items-center justify-between hover:bg-gray-50 transition-colors"
            aria-expanded={openId === faq.id}
          >
            <span className="text-gray-900">{faq.question}</span>
            <span className="text-2xl font-bold text-amaranth_purple-500 transition-transform duration-200" style={{ transform: openId === faq.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              {openId === faq.id ? '−' : '+'}
            </span>
          </button>

          {/* ANSWER: Collapsible content */}
          {openId === faq.id && (
            <div className="p-4 pt-0 text-gray-700 animate-fadeInDown">
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
